import React, { FC } from 'react'
import NonFlexingContainer from '@sport1/news-styleguide/NonFlexingContainer'
import CardHeadline from '@sport1/news-styleguide/CardHeadline'
import { MatchElementsCardContentProps, MatchElementsCardProps } from '@sport1/types/web'
import { WidgetTrackingIdentifier } from '@sport1/bets-service-client'
import { MatchOverlayProvider } from '@/utils/overlay/MatchOverlayProvider'
import HorizontalSlider from '@/components/HorizontalSlider'
import MatchCardItem from '@/components/MatchElementsCard/util'
import { getDateFromZeroTimezoneStamp } from '@/utils/date'
import OddsDisclaimer from '@/components/OddsDisclaimer'
import { onArrowPress } from '@/helpers/pageReferringWidgetHelper'
import { useFindValueInNavigation, useIsOnTeamPage } from '@/helpers/teamHelper'
import { MATCH_ELEMENTS_CARD_ITEM_WIDTH } from '@/components/HorizontalSlider'
import TeamPageCTAButton from '@/components/TeamPageCTAButton'

type Props = Omit<MatchElementsCardProps, 'type' | 'isHorizontal'> & {
    trackingInteractionLabel: string
    trackingCreation?: WidgetTrackingIdentifier
    testID?: string
}

const HorizontalMatchElementsCards: FC<Props> = ({
    trackingInteractionLabel,
    trackingCreation,
    headline,
    content,
    testID = 'match-elements-card',
}) => {
    const isOnTeamPage = useIsOnTeamPage()
    const gamePlanButtonUrl = useFindValueInNavigation('web-team-gameplan')

    return (
        <NonFlexingContainer marginBottom="spacing-11" testID={`${testID}-outerContainer`}>
            <NonFlexingContainer
                className="md:bg-white"
                padding={['spacing-6', 'spacing-6', 'spacing-9', 'spacing-11']}
                testID={`${testID}-innerContainer`}
            >
                <MatchOverlayProvider>
                    <NonFlexingContainer
                        testID={`${testID}-thirdContainer`}
                        marginBottom="spacing-6"
                    >
                        <CardHeadline
                            testID={`${testID}-headline`}
                            cardHeadlineType={['cardHeadlineS', 'cardHeadlineM', 'cardHeadlineM']}
                            text={headline}
                            href={isOnTeamPage ? (gamePlanButtonUrl?.href as string) : undefined}
                            onPress={isOnTeamPage ? onArrowPress : undefined} // triggers page_referring_widget
                            rightIconName="arrow_forward_ios"
                        />
                    </NonFlexingContainer>
                    <NonFlexingContainer
                        position="relative"
                        testID={`${testID}-fourthContainer`}
                        padding={['spacing-6', 'spacing-6', 'spacing-6', 'spacing-none']}
                        backgroundColor="pearl"
                    >
                        <HorizontalSlider<MatchElementsCardContentProps>
                            items={content}
                            renderItem={({ item }) => {
                                const { matchClientModel, tags } = item
                                const tag = tags?.length ? tags[0] : undefined
                                const startTime = matchClientModel.scheduledStartTime
                                    ? `, ${getDateFromZeroTimezoneStamp(
                                          matchClientModel.scheduledStartTime
                                      )}`
                                    : ''
                                return (
                                    <MatchCardItem
                                        match={matchClientModel}
                                        headline={`${item.headline}${startTime}`}
                                        tag={tag}
                                        props={{
                                            spaceTop: 'small',
                                            width: MATCH_ELEMENTS_CARD_ITEM_WIDTH,
                                            trackingInteractionLabel,
                                            trackingCreation,
                                        }}
                                    />
                                )
                            }}
                            keyExtractor={item => `${item.headline}-${item.componentKey}`}
                            horizontalScrollbarEnabled
                            backgroundColor="pearl"
                            spaceX={['standard', 'medium', 'big']}
                        />
                    </NonFlexingContainer>
                    <OddsDisclaimer textAlign="left" marginTop="spacing-6" />
                </MatchOverlayProvider>
                {isOnTeamPage ? (
                    <TeamPageCTAButton
                        buttonText="Alle Spiele"
                        ctaButtonUrl={gamePlanButtonUrl}
                        testID={testID}
                    />
                ) : null}
            </NonFlexingContainer>
        </NonFlexingContainer>
    )
}

export default HorizontalMatchElementsCards
